import React, { useEffect, useState } from 'react';
import { ClaimContext, ClaimData } from '../../context/claim';
import { useRouteMatch } from '../../util/useRouteMatch';
import TreeNode, {
	TreeData,
	TreeItemData,
	TreeItemInterface,
} from '../layouts/SidePanel/TreeNode';

interface ClaimRoutes {
	ROOT: string;
	POLICY_DETAILS: string;
	AUX_DATA: string;
	ADD_SUBCLAIM: string;
	SUBCLAIMS: string;
	SUBCLAIM: string;
	SUBCLAIM_AUX_DATA: string;
}

const ROUTES: ClaimRoutes = {
	ROOT: '/policy/:policyId/claims/:claimId',
	POLICY_DETAILS: '/policy/:policyId/claims/:claimId/details',
	AUX_DATA: '/policy/:policyId/claims/:claimId/auxData',
	ADD_SUBCLAIM: '/policy/:policyId/claims/:claimId/addSubclaim',
	SUBCLAIMS: '/policy/:policyId/claims/:claimId/subclaims',
	SUBCLAIM: '/policy/:policyId/claims/:claimId/subclaims/:subclaimId',
	SUBCLAIM_AUX_DATA:
		'/policy/:policyId/claims/:claimId/subclaims/:subclaimId/auxData',
};

interface Props {
	claimData: ClaimData;
}

const ClaimsSidebar = (props: Props) => {
	const { claimData } = props;
	const { claim } = claimData;
	const { currentStatus, subClaims } = claim;
	const routeMatch = useRouteMatch(
		Object.keys(ROUTES).map((key) => ROUTES[key as keyof ClaimRoutes]),
	);
	const params = routeMatch?.params || {};
	const routeName = routeMatch?.pattern?.path || '';
	const { policyId, claimId, subclaimId } = params;
	const [itemsExpanded, setItemsExpanded] = useState([]);

	const claimLink = `/policy/${policyId}/claims/${claimId}`;
	const detailsLink = `${claimLink}/details`;
	const subclaimsLink = `${claimLink}/subclaims`;

	const getInitialExpandedItems = () => {
		const ids = ['Subclaims'];

		if (subclaimId) {
			ids.push(subclaimId);
		}
		return ids;
	};

	useEffect(() => {
		const initialIds = getInitialExpandedItems();
		setItemsExpanded(initialIds);
	}, [subclaimId]);

	const getRootItem = () => {
		const children = ['Details'];
		if (currentStatus === 'open' || currentStatus === 'closed') {
			children.push('Subclaims');
		}

		return {
			id: 'root',
			children,
			hasChildren: true,
			isExpanded: true,
			isChildrenLoading: false,
			data: {
				title: 'root',
			},
		} as TreeItemInterface;
	};

	const getItems = () => {
		const isDetailsSelected =
			!subclaimId &&
			(routeName === ROUTES.POLICY_DETAILS || routeName === ROUTES.AUX_DATA);

		let items: TreeItemData = {
			root: getRootItem(),
			Details: {
				id: 'Details',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Details',
					link: detailsLink,
					selected: isDetailsSelected,
				},
			},
		};

		const subclaimsListItem: TreeItemInterface = {
			id: 'Subclaims',
			children: [],
			hasChildren: !!subClaims.length,
			isExpanded: itemsExpanded.includes('Subclaims'),
			isChildrenLoading: false,
			data: {
				title: 'Subclaims',
				link: subclaimsLink,
				selected: routeName === ROUTES.SUBCLAIMS,
				hasError: false,
			},
		};

		const subclaimItems = subClaims.reduce(
			(sublclaimItems: TreeItemData, subclaim) => {
				const subclaimLocator = subclaim.locator;
				const subclaimLink = `${subclaimsLink}/${subclaimLocator}`;
				const isSubclaimLocatorSelected =
					!!subclaimId &&
					subclaimLocator === subclaimId &&
					(routeName === ROUTES.SUBCLAIM ||
						routeName === ROUTES.SUBCLAIM_AUX_DATA);

				const subclaimItem: TreeItemInterface = {
					id: subclaimLocator,
					children: [],
					hasChildren: false,
					isExpanded: false,
					isChildrenLoading: false,
					data: {
						title: subclaimLocator,
						link: subclaimLink,
						className: 'subclaim',
						selected: isSubclaimLocatorSelected,
					},
				};

				sublclaimItems[subclaimLocator] = subclaimItem;
				subclaimsListItem.children.push(subclaimLocator);

				return sublclaimItems;
			},
			{},
		);

		items = {
			...items,
			...subclaimItems,
			Subclaims: subclaimsListItem,
		};

		return items;
	};

	const initializeTree = () => {
		const sidePanelData: TreeData = {
			rootId: 'root',
			items: getItems(),
		};

		return sidePanelData;
	};
	const tree = initializeTree();

	return <TreeNode defaultExpanded={itemsExpanded} treeData={tree} />;
};

function withContext<T>(Component: any) {
	return (props: Omit<T, 'claimData'>) => (
		<ClaimContext.Consumer>
			{(claimData) => <Component {...props} claimData={claimData} />}
		</ClaimContext.Consumer>
	);
}

export default withContext<Props>(ClaimsSidebar);
